import { PayloadAction, createSlice } from '@reduxjs/toolkit'

import { ArtistRolesEnum, Roles } from 'src/Constants/enums'
import { ApiError, ErrorCode, NameSpace, SliceStatus } from 'src/Redux/types'
import { setApiError } from 'src/Hooks/redux'
import { userInfoApi } from 'src/Redux/userInfoApi'
import {
  ArtistUserMetaData,
  CreatorUserMetaData,
  CuratorUserMetaData,
  User,
  UsersMeResponse,
} from 'src/Types/users'

import { acceptTermsNew, getSpotifyAvatar } from './api-actions'

export interface UserState {
  user: User
  referralCode: null | string
  hasPassword: boolean
  isEmailBounced: boolean
  verified: boolean | null
  role: Roles | null
  phone_number?: string | null
  error: ApiError | null
  status: SliceStatus
  getUserStatus: SliceStatus
}

const initialState: UserState = {
  user: {
    isReferral: false,
    intercomHash: null,
    balance: 0,
    isBlocked: false,
    first_name: '',
    last_name: '',
    lastActivity: '',
    last_activity: '',
    ip: '',
    isArtist: false,
    is_email_confirmed: false,
    is_first_campaign: false,
    isEmailConfirmed: false,
    are_terms_accepted: null,
    createdAt: '',
    email: '',
    geo: {
      country: '',
    },
    country: '',
    id: '',
    phone_number: '',
    role: Roles.artist,
    terms_position: null,
    playlist_requirement: false,
    spotifyId: null,
    influencerId: 0,
    followerCount: 0,
  },
  referralCode: null,
  hasPassword: false,
  isEmailBounced: false,
  verified: false,
  role: null,
  phone_number: null,
  error: null,
  status: SliceStatus.Idle,
  getUserStatus: SliceStatus.Idle,
}

const getRoleMetaData = <T>(payload: UsersMeResponse, role: Roles): T | null =>
  payload.role === role ? (payload.metaData as T | null) : null

const getIsNewTerm = (metaData: CuratorUserMetaData | null): boolean =>
  Boolean(metaData && !metaData.isBlocked && !metaData.inBlacklist)

const getArtistState = (metaData: ArtistUserMetaData | null): User['artist'] =>
  metaData ? { artist_role: metaData.role } : void 0

const mapCommonUserFields = (user: User, payload: UsersMeResponse): User => ({
  ...user,
  user_photo_url: payload.userPhotoUrl,
  first_name: payload.firstName ?? '',
  last_name: payload.lastName ?? '',
  createdAt: payload.createdAt,
  updatedAt: payload.updatedAt,
  email: payload.email,
  spotifyId: payload.spotifyId ?? null,
  is_email_confirmed: payload.isEmailConfirmed,
  isEmailConfirmed: payload.isEmailConfirmed,
  lastActivity: payload.lastActivity ?? '',
  id: payload.id,
  intercomHash: payload.intercomHash,
  isReferral: payload.isReferral,
  balance: payload.balance,
  country: payload.countryCode,
  phone_number: payload.phoneNumber ?? '',
  role: payload.role,
})

const mapRoleUserFields = (
  user: User,
  artistMetaData: ArtistUserMetaData | null,
  curatorMetaData: CuratorUserMetaData | null,
  creatorMetaData: CreatorUserMetaData | null,
): User => ({
  ...user,
  influencerId: creatorMetaData?.id ?? 0,
  isBlocked: creatorMetaData?.isBlocked ?? false,
  followerCount: creatorMetaData?.followerCount ?? 0,
  are_terms_accepted: curatorMetaData?.areTermsAccepted ?? null,
  terms_position: curatorMetaData?.termsPosition ?? null,
  is_new_term: getIsNewTerm(curatorMetaData),
  artist: getArtistState(artistMetaData),
})

export const userSlice = createSlice({
  initialState,
  name: NameSpace.User,
  reducers: {
    resetUserBalance: (state) => {
      state.user.balance = 0
    },
    setIsFirstCampaign: (state, { payload }: PayloadAction<boolean>) => {
      state.user.is_first_campaign = payload
    },
    setArtistRole: (state, { payload }: PayloadAction<ArtistRolesEnum>) => {
      state.user.artist = { ...state.user.artist, artist_role: payload }
    },
    clearUserState: () => initialState,
  },
  extraReducers: (builder) => {
    // acceptTermsNew
    builder.addCase(acceptTermsNew.pending, (state) => {
      state.status = SliceStatus.Loading
    })
    builder.addCase(acceptTermsNew.fulfilled, (state, { payload }) => {
      state.status = SliceStatus.Succeeded
      state.user = {
        ...state.user,
        terms_position: payload.termsPosition,
        are_terms_accepted: payload.areTermsAccepted,
      }
    })
    builder.addCase(acceptTermsNew.rejected, (state, action) => {
      state.status = SliceStatus.Failed
      state.error = setApiError(action)
    })

    // getSpotifyAvatar
    builder.addCase(getSpotifyAvatar.pending, (state) => {
      state.status = SliceStatus.Loading
    })
    builder.addCase(getSpotifyAvatar.fulfilled, (state, { payload }) => {
      state.status = SliceStatus.Succeeded
      state.user = {
        ...state.user,
        user_photo_url: payload.new_avatar_image,
      }
    })
    builder.addCase(getSpotifyAvatar.rejected, (state, action) => {
      state.status = SliceStatus.Failed
      state.error = setApiError(action)
    })

    builder.addMatcher(userInfoApi.endpoints.usersMe.matchPending, (state) => {
      state.status = SliceStatus.Loading
      state.getUserStatus = SliceStatus.Loading
    })
    builder.addMatcher(
      userInfoApi.endpoints.usersMe.matchFulfilled,
      (state, { payload }) => {
        const artistMetaData = getRoleMetaData<ArtistUserMetaData>(
          payload,
          Roles.artist,
        )
        const curatorMetaData = getRoleMetaData<CuratorUserMetaData>(
          payload,
          Roles.curator,
        )
        const creatorMetaData = getRoleMetaData<CreatorUserMetaData>(
          payload,
          Roles.influencer,
        )

        state.status = SliceStatus.Succeeded
        state.getUserStatus = SliceStatus.Succeeded
        state.role = payload.role
        state.hasPassword = payload.hasPassword
        state.isEmailBounced = payload.isEmailBounced
        state.verified = curatorMetaData?.verified ?? null
        state.phone_number = payload.phoneNumber
        state.referralCode = payload.referralCode
        state.user = mapRoleUserFields(
          mapCommonUserFields(state.user, payload),
          artistMetaData,
          curatorMetaData,
          creatorMetaData,
        )
      },
    )
    builder.addMatcher(
      userInfoApi.endpoints.usersMe.matchRejected,
      (state, { payload, error, meta }) => {
        if (meta.condition) {
          return
        }

        state.status = SliceStatus.Failed
        state.getUserStatus = SliceStatus.Failed
        state.error = {
          message: error.message ?? '',
          code:
            typeof payload?.status === 'number'
              ? payload.status
              : ErrorCode.InternalServerError,
        }
      },
    )
  },
})

export const {
  resetUserBalance,
  setIsFirstCampaign,
  setArtistRole,
  clearUserState,
} = userSlice.actions
